import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../../api/http'
import { login } from '../../auth/authApi'
import { googleConfig, googleLink, googleLogin, googleRegister } from '../../auth/googleApi'
import type { AuthResponse } from '../../auth/types'
import { getLocale } from '../../i18n'
import { Button, Field } from '../../components/ui'
import { GoogleSignInButton } from './GoogleSignInButton'

type Mode = 'start' | 'choose' | 'register' | 'link'

export function GoogleAuthFlow({ intent, onSuccess, onActiveChange, disabled = false }: {
  intent: 'login' | 'register'
  onSuccess: (response: AuthResponse) => void
  onActiveChange: (active: boolean) => void
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const [config, setConfig] = useState<{ enabled: boolean; clientId: string } | null>(null)
  const [configFailed, setConfigFailed] = useState(false)
  const [configAttempt, setConfigAttempt] = useState(0)
  const [mode, setMode] = useState<Mode>('start')
  const [fullName, setFullName] = useState('')
  const [organization, setOrganization] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [session, setSession] = useState<AuthResponse | null>(null)
  const [prepared, setPrepared] = useState(false)
  const [busy, setBusy] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    googleConfig().then(value => { if (active) setConfig(value) })
      .catch(() => { if (active) setConfigFailed(true) })
    return () => { active = false }
  }, [configAttempt])

  useEffect(() => { onActiveChange(mode !== 'start' || busy) }, [mode, busy, onActiveChange])

  function changeMode(next: Mode) {
    setMode(next)
    setPrepared(false)
    setPassword('')
    setError(null)
    setAttempt(value => value + 1)
  }

  function showError(caught: unknown) {
    const code = caught instanceof ApiClientError ? caught.code : ''
    if (code === 'GOOGLE_NOT_LINKED' || code === 'GOOGLE_LINK_REQUIRED') {
      changeMode('choose')
      setError(t('auth.google.notLinked'))
    } else if (code === 'PASSWORD_EXPIRED') {
      setError(t('auth.login.errors.expired'))
    } else if (code === 'INVALID_CREDENTIALS' || code === 'UNAUTHORIZED') {
      setError(t('auth.login.errors.invalidCredentials'))
    } else if (code === 'GOOGLE_LINK_CONFLICT') {
      setError(t('auth.google.conflict'))
    } else {
      setError(t('auth.google.failed'))
    }
    setAttempt(value => value + 1)
  }

  async function prepare(event: FormEvent) {
    event.preventDefault()
    setError(null)
    if (mode === 'register') {
      if (!fullName.trim() || !organization.trim()) { setError(t('auth.google.missingDetails')); return }
      setPrepared(true)
      return
    }
    if (!email.trim() || !password) { setError(t('auth.login.errors.missingFields')); return }
    setBusy(true)
    try {
      setSession(await login({ email: email.trim(), password }))
      setPrepared(true)
    } catch (caught) { showError(caught) } finally { setBusy(false) }
  }

  async function handleCredential(credential: string) {
    setBusy(true)
    setError(null)
    try {
      if (mode === 'register') {
        onSuccess(await googleRegister(credential, fullName.trim(), organization.trim(), getLocale()))
      } else if (mode === 'link' && session) {
        await googleLink(credential, password, session.accessToken)
        setPassword('')
        onSuccess(session)
      } else {
        onSuccess(await googleLogin(credential))
      }
    } catch (caught) {
      showError(caught)
      setBusy(false)
    }
  }

  if (disabled) return null
  if (!config) return configFailed ? <div role="status" style={{ display: 'grid', gap: 'var(--sp-2)' }}>
    {t('auth.google.unavailable')}
    <Button kind="ghost" onClick={() => { setConfigFailed(false); setConfigAttempt(value => value + 1) }}>{t('auth.google.retry')}</Button>
  </div> : null
  if (!config.enabled) return null

  const showGoogle = (mode === 'start' && intent === 'login') || prepared
  return <section aria-label={t('auth.google.title')} style={{ display: 'grid', gap: 'var(--sp-4)', minWidth: 0 }}>
    {error && <div role="alert" style={{ color: 'var(--danger)', fontSize: 'var(--fs-14)' }}>{error}</div>}
    {mode === 'start' && intent === 'register' && <Button kind="secondary" onClick={() => changeMode('register')}>{t('auth.google.signUp')}</Button>}
    {mode === 'choose' && <>
      <Button onClick={() => changeMode('register')}>{t('auth.google.create')}</Button>
      <Button kind="secondary" onClick={() => changeMode('link')}>{t('auth.google.connect')}</Button>
    </>}
    {(mode === 'register' || mode === 'link') && !prepared && <form onSubmit={prepare} style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      <p style={{ margin: 0, color: 'var(--fg-muted)' }}>{t(mode === 'register' ? 'auth.google.signupHint' : 'auth.google.linkHint')}</p>
      {mode === 'register' ? <>
        <Field label={t('auth.register.fullName')} maxLength={160} value={fullName} onChange={event => setFullName(event.target.value)} />
        <Field label={t('auth.register.organizationName')} maxLength={160} value={organization} onChange={event => setOrganization(event.target.value)} />
      </> : <>
        <Field label={t('auth.login.email')} type="email" value={email} onChange={event => setEmail(event.target.value)} disabled={busy} />
        <Field label={t('auth.login.password')} type="password" value={password} onChange={event => setPassword(event.target.value)} disabled={busy} />
      </>}
      <Button type="submit" disabled={busy}>{t(busy ? 'auth.login.submitting' : 'auth.google.continue')}</Button>
    </form>}
    {prepared && <p style={{ margin: 0, color: 'var(--fg-muted)', overflowWrap: 'anywhere' }}>
      {mode === 'register' ? t('auth.google.confirmSignup', { organization }) : t('auth.google.confirmLink', { email: session?.user.email })}
    </p>}
    {showGoogle && !busy && <GoogleSignInButton key={`${mode}-${attempt}`} clientId={config.clientId} onCredential={credential => { void handleCredential(credential) }} />}
    {busy && <div role="status">{t('auth.login.submitting')}</div>}
    {session ? <Button kind="ghost" disabled={busy} onClick={() => { setPassword(''); onSuccess(session) }}>{t('auth.google.skipLink')}</Button>
      : mode !== 'start' && <Button kind="ghost" disabled={busy} onClick={() => changeMode('start')}>{t('auth.google.back')}</Button>}
    {mode === 'start' && <div style={{ textAlign: 'center', color: 'var(--fg-subtle)', fontSize: 'var(--fs-13)' }}>{t('auth.google.orEmail')}</div>}
  </section>
}
